// src/services/dashboard.ts
//
// One typed place for the dashboard's calls. The previous arrangement had
// Dashboard.tsx building URLs inline and a services/queue.ts pointing at routes
// that had never existed, which is how four of them ended up 404ing unnoticed.

import { apiClient } from './api';
import { Email } from '../types';
import { CampaignSummary, StageCounts } from '../types/stage';

export interface QueueQuery {
  page?: number;
  pageSize?: number;
  stage?: string;
  /** 'queue' shows active work, 'history' shows finished. */
  view?: 'queue' | 'history';
  held?: boolean;
  priority?: number;
  clientReferencePrefix?: string;
  search?: string;
}

export interface QueuePage {
  emails: Email[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export type BulkAction = 'pause' | 'resume' | 'cancel' | 'delete' | 'retry' | 'archive';

/** Describes a set of emails without listing their ids. */
export interface BulkSelector {
  stage?: string;
  priority?: number;
  queuedBefore?: string;
  queuedAfter?: string;
  clientReferencePrefix?: string;
  search?: string;
}

export interface BulkResult {
  affected: number;
  action: string;
  matched: number;
}

class DashboardService {
  async getQueue(query: QueueQuery): Promise<QueuePage> {
    const response = await apiClient.get<QueuePage>('/dashboard/queue', { params: query });
    return response.data;
  }

  async getStageCounts(clientReferencePrefix?: string): Promise<StageCounts> {
    const response = await apiClient.get<StageCounts>('/dashboard/queue/stages', {
      params: clientReferencePrefix ? { clientReferencePrefix } : undefined,
    });
    return response.data;
  }

  async getCampaigns(take = 25): Promise<{ campaigns: CampaignSummary[] }> {
    const response = await apiClient.get<{ campaigns: CampaignSummary[] }>('/dashboard/campaigns', {
      params: { take },
    });
    return response.data;
  }

  async retryEmail(emailId: string): Promise<void> {
    await apiClient.post(`/dashboard/emails/${emailId}/retry`);
  }

  async deleteEmail(emailId: string): Promise<void> {
    await apiClient.delete(`/dashboard/emails/${emailId}`);
  }

  /**
   * Act on a named list of emails. expectedCount is the length of that list, so
   * the server can confirm nothing shifted underneath the selection.
   */
  async bulkByIds(action: BulkAction, emailIds: string[]): Promise<BulkResult> {
    const response = await apiClient.post<BulkResult>('/dashboard/queue/bulk', {
      action,
      emailIds,
      expectedCount: -1,
    });
    return response.data;
  }

  /**
   * Act on everything matching a filter. This is the one that makes a bulk run
   * manageable — no paging through 50,000 rows to select them.
   *
   * expectedCount is what the operator was shown when they confirmed; the server
   * returns 409 if the set has moved since.
   */
  async bulkBySelector(
    action: BulkAction,
    selector: BulkSelector,
    expectedCount: number
  ): Promise<BulkResult> {
    const response = await apiClient.post<BulkResult>('/dashboard/queue/bulk', {
      action,
      selector,
      expectedCount,
    });
    return response.data;
  }
}

export const dashboardService = new DashboardService();
